import {Card, Tabs} from 'platform/components';
import {Heading, HStack, Icon, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {useGetDefinitionTemplateListQuery, useGetDocumentGroupListQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import i18n from '@dms/i18n';
import {SettingsTemplate} from '@dms/settings/shared';
import {useTenant} from '@dms/shared';

import {AuthorizationProfileCard} from './components/AuthorizationProfileCard';
import {
  ALL_LOCALES_VALUE,
  LocaleChipValue,
  LocaleFilterChips,
} from './components/LocaleFilterChips';
import {useGetTemplateGroupStructure} from './hooks/useGetTemplateGroupStructure';
import {filterDefinitionsByLocale} from './utils/filterDefinitionsByLocale';

export function TemplateManagement() {
  const {tenant, isMultiLocale, supportedLocales} = useTenant();
  const [selectedLocaleByGroup, setSelectedLocaleByGroup] = useState<
    Record<string, LocaleChipValue>
  >({});

  const {
    data: definitionTemplateList,
    isLoading: isLoadingDefinitionTemplateList,
    isError: isErrorDefinitionTemplateList,
  } = useGetDefinitionTemplateListQuery({});

  const {
    data: documentGroupList,
    isLoading: isLoadingDocumentGroupList,
    isError: isErrorDocumentGroupList,
  } = useGetDocumentGroupListQuery();

  const {
    data: authorizationProfiles,
    isLoading: isLoadingAuthorizationProfiles,
    isError: isErrorAuthorizationProfiles,
  } = useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id ?? ''}, {skip: isNil(tenant)});

  const [getTemplateGroupStructure] = useGetTemplateGroupStructure(
    definitionTemplateList,
    documentGroupList,
    authorizationProfiles
  );

  const isLoading =
    isLoadingDefinitionTemplateList || isLoadingDocumentGroupList || isLoadingAuthorizationProfiles;
  const isError =
    isErrorDefinitionTemplateList || isErrorDocumentGroupList || isErrorAuthorizationProfiles;

  return (
    <SettingsTemplate
      header={{title: i18n.t('page.settings.labels.templateManagement')}}
      data-testid="template-management"
      isLoading={isLoading}
      isError={isError}
    >
      <VStack spacing={4}>
        {getTemplateGroupStructure()?.map(({category, groups}) => (
          <Card key={category.id} variant="inlineGrey" colorStripe={category.colorStripe}>
            <HStack spacing={2} align="center">
              <Icon value={category.icon} size={5} />
              <Heading size={4}>{category.label}</Heading>
            </HStack>

            <Tabs
              tabs={groups.map(({group, definitions}) => {
                const groupLocale = selectedLocaleByGroup[group.id] ?? ALL_LOCALES_VALUE;
                const filteredDefinitions = filterDefinitionsByLocale(
                  definitions,
                  groupLocale === ALL_LOCALES_VALUE ? null : groupLocale
                );

                return {
                  id: group.id,
                  title: group.title,
                  content: (
                    <VStack spacing={2}>
                      {isMultiLocale && (
                        <LocaleFilterChips
                          value={groupLocale}
                          onChange={(next) =>
                            setSelectedLocaleByGroup((previous) => ({
                              ...previous,
                              [group.id]: next,
                            }))
                          }
                          supportedLocales={supportedLocales}
                        />
                      )}
                      {filteredDefinitions.map((definition) => (
                        <AuthorizationProfileCard key={definition.id} definition={definition} />
                      ))}
                    </VStack>
                  ),
                };
              })}
            />
          </Card>
        ))}
      </VStack>
    </SettingsTemplate>
  );
}
